import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, Dropdown } from 'flowbite-react';
import { useState } from 'react';
import { useLoaderData, useOutletContext } from 'react-router-dom';

import { LoaderData, loaders } from 'src/api/loaders.ts';
import type { Store } from 'src/api/models';
import { queries } from 'src/api/queries';
import { FeedbackTable } from 'src/components/feedback-table';
import { MetricsHeaderChart } from 'src/components/metrics-header-chart';
import { DashboardContext } from 'src/routes/dashboard/container';

export function AnalyticsSection({ store }: { store: Store }) {
  const dropdownLabels = {
    0: 'Lifetime',
    90: 'Last 90 Days',
    30: 'Last 30 Days',
  };
  const labelOrder: (keyof typeof dropdownLabels)[] = [0, 90, 30];
  const [currentAnalyticsInterval, setCurrentAnalyticsInterval] =
    useState<keyof typeof dropdownLabels>(0);

  const { data: aggregatedMetrics } = useQuery(
    queries.storeAggregatedMetrics(store.key_name, currentAnalyticsInterval),
  );

  const { isInitialLoading: isInitialLoadingDailyMetrics, data: dailyMetrics } = useQuery(
    queries.storeDailyMetrics(store.key_name, currentAnalyticsInterval),
  );

  return (
    <>
      {!isInitialLoadingDailyMetrics && dailyMetrics && (
        <Card className='w-full'>
          <div>
            {store.show_attribution_metrics && (
              <div className='inline-block text-left'>
                <span className='block text-4xl font-semibold text-gray-900'>
                  <FontAwesomeIcon
                    icon={solid('dollar-sign')}
                    className='mr-1 inline-block'
                    size='xs'
                  />
                  {Math.round((aggregatedMetrics?.centsDriven ?? 0) / 100).toLocaleString('en-US', {
                    maximumFractionDigits: 0,
                    useGrouping: true,
                  })}
                </span>
                <span className='text-sm text-gray-500'>Attributed Revenue</span>
              </div>
            )}
            <div className='ml-12 inline-block text-left'>
              <span className='block text-4xl font-semibold text-gray-900'>
                <FontAwesomeIcon icon={solid('envelope')} className='mr-1 inline-block' size='xs' />
                {(aggregatedMetrics?.sentMessages ?? 0).toLocaleString('en', {
                  useGrouping: true,
                })}
              </span>
              <span className='text-sm text-gray-500'>Delivered Messages</span>
            </div>
            <div className='ml-12 inline-block text-left'>
              <span className='block text-4xl font-semibold text-gray-900'>
                <FontAwesomeIcon
                  icon={solid('envelope-open')}
                  className='mr-1 inline-block'
                  size='xs'
                />
                {(aggregatedMetrics?.openRate ?? 0).toLocaleString('en-US', {
                  style: 'percent',
                  maximumFractionDigits: 1,
                  useGrouping: true,
                })}
              </span>
              <span className='text-sm text-gray-500'>Open Rate</span>
            </div>
            <div className='ml-12 inline-block text-left'>
              <span className='block text-4xl font-semibold text-gray-900'>
                <FontAwesomeIcon
                  icon={solid('mouse-pointer')}
                  className='mr-1 inline-block'
                  size='xs'
                />
                {(aggregatedMetrics?.clickRate ?? 0).toLocaleString('en-US', {
                  style: 'percent',
                  maximumFractionDigits: 1,
                  useGrouping: true,
                })}
              </span>
              <span className='text-sm text-gray-500'>Click Rate</span>
            </div>
            <Dropdown
              label={dropdownLabels[currentAnalyticsInterval]}
              renderTrigger={() => (
                <div className='float-right w-fit text-sm font-semibold text-gray-500 hover:text-gray-950'>
                  {dropdownLabels[currentAnalyticsInterval]}
                  <FontAwesomeIcon icon={solid('chevron-down')} size='sm' className='ml-2' />
                </div>
              )}
            >
              {labelOrder.map((dropdownLabel) => (
                <Dropdown.Item
                  key={dropdownLabel}
                  onClick={() => setCurrentAnalyticsInterval(dropdownLabel)}
                >
                  {dropdownLabels[dropdownLabel]}
                </Dropdown.Item>
              ))}
            </Dropdown>
          </div>
          <MetricsHeaderChart metrics={dailyMetrics} height={300} />
        </Card>
      )}
    </>
  );
}

export function DashboardHome() {
  const { store } = useOutletContext<DashboardContext>();
  const initialData = useLoaderData() as LoaderData<typeof loaders.usecasesForStoreLoader>;
  const storeKeyName = store?.key_name;
  const storeID = store?.id;
  const [feedbackPage, setFeedbackPage] = useState(1);

  const { isLoading: isLoadingFeedbacks, data: feedbacks } = useQuery({
    ...queries.customerFeedbacksForStore(storeID, feedbackPage, 25),
    placeholderData: keepPreviousData,
    enabled: !!storeID,
  });
  const {
    data: { results: usecases },
  } = useQuery({
    ...queries.usecasesForStore(storeKeyName),
    initialData: initialData,
    enabled: !!storeKeyName,
  });

  if (!store || !usecases || usecases.length === 0) {
    return null;
  }

  return (
    <>
      {!usecases.find(
        (uc) => uc.onboarding_state == 'LIVE' || uc.onboarding_state == 'STOPPED',
      ) && (
        <>
          <Card className='mt-6'>
            <div className='flex w-full items-center justify-center'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Check back for metrics when your videos are live
              </h2>
            </div>
          </Card>
        </>
      )}
      <AnalyticsSection store={store} />
      {!usecases.find(
        (uc) => uc.onboarding_state == 'LIVE' || uc.onboarding_state == 'STOPPED',
      ) && (
        <>
          <Card className='mt-6'>
            <div className='flex w-full items-center justify-center'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Check back for customer responses when your videos are live
              </h2>
            </div>
          </Card>
        </>
      )}
      {usecases.find((uc) => uc.onboarding_state == 'LIVE' || uc.onboarding_state == 'STOPPED') && (
        <>
          <Card className='mt-6'>
            <div className='flex w-full'>
              <h2 className='mb-2 font-semibold text-gray-900'>
                {feedbacks?.count}
                <span className='text-gray-500'> Customer Responses</span>
              </h2>
            </div>
            <FeedbackTable
              isLoading={isLoadingFeedbacks}
              feedbacks={feedbacks?.results ?? []}
              currentPage={feedbackPage}
              setPage={setFeedbackPage}
              totalPages={Math.ceil((feedbacks?.count ?? 1) / 10)}
            />
          </Card>
        </>
      )}
    </>
  );
}
